import { useEventListener } from "@core/features/eventBus";
import { videoEventBus } from "@core/features/video";
import { useTracking } from "@core/features/tracking";
import { usePlatformStore, useVideoMeetingDetails } from "@platform/store";

export function VideoEventListeners() {
  const { trackEvent } = useTracking();
  const user = usePlatformStore((s) => s.user);
  const { meetingId } = useVideoMeetingDetails();

  const getAppVersion = (): string => "1.0.0";

  // Helper function to get base analytics params (with video context)
  // TODO: Fetch program data for video analytics
  const getBaseParams = () => ({
    user_id: user?.id?.toString() || "unknown",
    meeting_id: meetingId || "unknown",
    program_id: "unknown",
    app_version: getAppVersion(),
    timestamp: new Date().toISOString(),
  });

  // Video Analytics Event Listeners
  useEventListener(videoEventBus, "videochat:joined", ({ meetingId }) => {
    trackEvent({
      name: "videochat_joined",
      params: { ...getBaseParams(), meeting_id: meetingId },
    });
  });

  useEventListener(videoEventBus, "videochat:left", ({ meetingId }) => {
    trackEvent({
      name: "videochat_left",
      params: { ...getBaseParams(), meeting_id: meetingId },
    });
  });

  useEventListener(videoEventBus, "videochat:muted", () => {
    trackEvent({
      name: "videochat_muted",
      params: getBaseParams(),
    });
  });

  useEventListener(videoEventBus, "videochat:unmuted", () => {
    trackEvent({
      name: "videochat_unmuted",
      params: getBaseParams(),
    });
  });

  useEventListener(videoEventBus, "videochat:camera:on", () => {
    trackEvent({
      name: "videochat_camera_on",
      params: getBaseParams(),
    });
  });

  useEventListener(videoEventBus, "videochat:camera:off", () => {
    trackEvent({
      name: "videochat_camera_off",
      params: getBaseParams(),
    });
  });

  useEventListener(
    videoEventBus,
    "videochat:audio:source:selected",
    ({ deviceId }) => {
      trackEvent({
        name: "videochat_audio_source_selected",
        params: { ...getBaseParams(), device_id: deviceId },
      });
    }
  );

  useEventListener(
    videoEventBus,
    "videochat:video:source:selected",
    ({ deviceId }) => {
      trackEvent({
        name: "videochat_video_source_selected",
        params: { ...getBaseParams(), device_id: deviceId },
      });
    }
  );

  useEventListener(videoEventBus, "videochat:screen:shared", () => {
    trackEvent({
      name: "videochat_screen_shared",
      params: getBaseParams(),
    });
  });

  useEventListener(videoEventBus, "videochat:screen:unshared", () => {
    trackEvent({
      name: "videochat_screen_unshared",
      params: getBaseParams(),
    });
  });

  useEventListener(videoEventBus, "videochat:participant:viewed", () => {
    trackEvent({
      name: "videochat_participant_viewed",
      params: getBaseParams(),
    });
  });

  useEventListener(videoEventBus, "videochat:materials:opened", () => {
    trackEvent({
      name: "videochat_materials_opened",
      params: getBaseParams(),
    });
  });

  useEventListener(videoEventBus, "transcription:started", () => {
    trackEvent({
      name: "transcription_started",
      params: getBaseParams(),
    });
  });

  useEventListener(
    videoEventBus,
    "materials:article:viewed",
    ({ articleId }) => {
      trackEvent({
        name: "materials_article_viewed",
        params: { ...getBaseParams(), article_id: articleId },
      });
    }
  );

  useEventListener(videoEventBus, "materials:video:play", ({ videoId }) => {
    trackEvent({
      name: "materials_video_play",
      params: { ...getBaseParams(), video_id: videoId },
    });
  });

  useEventListener(videoEventBus, "materials:video:pause", ({ videoId }) => {
    trackEvent({
      name: "materials_video_pause",
      params: { ...getBaseParams(), video_id: videoId },
    });
  });

  useEventListener(videoEventBus, "materials:video:seek", ({ videoId }) => {
    trackEvent({
      name: "materials_video_seek",
      params: { ...getBaseParams(), video_id: videoId },
    });
  });

  useEventListener(videoEventBus, "action:opened", ({ stepId }) => {
    trackEvent({
      name: "action_opened",
      params: { ...getBaseParams(), step_id: stepId },
    });
  });

  useEventListener(videoEventBus, "action:completed", ({ stepId }) => {
    trackEvent({
      name: "action_completed",
      params: { ...getBaseParams(), step_id: stepId },
    });
  });

  useEventListener(
    videoEventBus,
    "videochat:active:speaker:started",
    ({ userId }) => {
      trackEvent({
        name: "videochat_active_speaker_started",
        params: { ...getBaseParams(), user_id: userId },
      });
    }
  );

  useEventListener(
    videoEventBus,
    "videochat:active:speaker:stopped",
    ({ userId }) => {
      trackEvent({
        name: "videochat_active_speaker_stopped",
        params: { ...getBaseParams(), user_id: userId },
      });
    }
  );

  useEventListener(videoEventBus, "videochat:error", ({ errorType }) => {
    trackEvent({
      name: "videochat_error",
      params: { ...getBaseParams(), error_type: errorType },
    });
  });

  return null;
}
